import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Collapse } from '@mui/material'
import { useFormikContext } from 'formik'
import React from 'react'
import { SettingsBox } from 'src/Elements/Data'
import { SingleToggleSettingBox } from 'src/Elements/SystemSettings/forms'
import { NumberField } from 'src/Libs/inputs/formik'
import { UnitTypes } from 'src/Libs/types'
import { DynamicPricesModel } from '../../types'
import { AccordionItem } from '@e3dc-react/shell/Elements/Accordion'
import { makeStyles } from 'tss-react/mui'

const useStyles = makeStyles()(theme => ({
  accordion: {
    /** for the content only */
    '.MuiAccordionDetails-root': {
      padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    },
    '&.Mui-expanded , :not(&.Mui-expanded)': {
      marginBottom: theme.spacing(2),
      marginTop: 0,
    },
  },
}))

interface Props {
  installationId?: string
  isDppWallboxToggleDisabled: boolean
  hasWritingRights?: boolean
}

const FORM_DEPTH = 1

/**
 * This component renders the dynamic prices form fields related to the electrical vehicle.
 */
const WallboxFormContent: React.FC<Props> = ({ isDppWallboxToggleDisabled, hasWritingRights = false }) => {
  const { t } = useCustomTranslation()
  const { classes } = useStyles()
  const { values } = useFormikContext<DynamicPricesModel>()

  const accordionTitle = t('systemSettings.price-based-charging.wallbox.section-title')

  return (
    <AccordionItem className={classes.accordion} title={accordionTitle} depth={FORM_DEPTH} elevation={0} expanded={true}>
      <SingleToggleSettingBox
        title={t('systemSettings-dynamic-prices.wallbox.toggle-wallbox-dynamic-power-prices')}
        name="dynamicPricesForWallboxesEnabled"
        disabled={!hasWritingRights || isDppWallboxToggleDisabled}
        description={t('systemSettings.dynamic-prices.dynamic-electricity-tariffs.description')}
        depth={FORM_DEPTH}
        tooltipText={isDppWallboxToggleDisabled ? t('systemSettings-dynamic-prices.wallbox.dpp-not-available') : ''}
      />
      <Collapse in={values.dynamicPricesForWallboxesEnabled}>
        {fields.map(({ titleKey, descriptionKey, fieldName, fieldSuffix, isDecimal, step, precision }) => (
          <SettingsBox
            key={titleKey}
            title={t(titleKey)}
            description={t(descriptionKey)}
            disabled={!hasWritingRights}
            depth={FORM_DEPTH}
            suffix={
              <NumberField
                name={fieldName}
                suffix={fieldSuffix}
                isDecimal={isDecimal}
                step={step}
                precision={precision}
                disabled={!hasWritingRights}
              />
            }
          />
        ))}
      </Collapse>
    </AccordionItem>
  )
}

export default WallboxFormContent

const fields = [
  {
    titleKey: 'systemSettings-dynamic-prices.wallbox.price-limit.title',
    descriptionKey: 'systemSettings-dynamic-prices.wallbox.price-limit.description',
    fieldName: 'priceLimitWallbox',
    fieldSuffix: UnitTypes.centsPerKiloWattHour,
    isDecimal: true,
    step: 0.001,
    precision: 3,
  },
]
